// API-client voor ZBS Vooropname backend.
//
// Hergebruikt @rvdh/shared-http-fetch requestHandler met 2-min timeout,
// 429-retry, JSON-error-code-hoisting en 401-global-handler.
//
// Base-URL komt uit VITE_API_BASE_URL (default: '' = same-origin, Vite proxy
// doet /api → backend tijdens dev).

import {
    requestHandler,
    setUnauthorizedHandler,
    registerErrorCodeHandler,
} from '@rvdh/shared-http-fetch';

const BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

const url = (path) => `${BASE}${path}`;

// Thin wrapper die automatisch BASE prepend; blijft requestHandler-API
// trouw zodat 401-callbacks werken.
export const api = {
    get: (path, opts) => requestHandler(url(path), 'GET', null, opts),
    post: (path, data, opts) => requestHandler(url(path), 'POST', data, opts),
    patch: (path, data, opts) => requestHandler(url(path), 'PATCH', data, opts),
    put: (path, data, opts) => requestHandler(url(path), 'PUT', data, opts),
    del: (path, opts) => requestHandler(url(path), 'DELETE', null, opts),

    /** Multipart upload (foto's). Omzeilt requestHandler omdat die JSON-stringify forceert. */
    async postMultipart(path, formData, { signal } = {}) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);
        const onAbort = () => controller.abort();
        if (signal) {
            if (signal.aborted) { controller.abort(); }
            else { signal.addEventListener('abort', onAbort); }
        }
        try {
            const res = await fetch(url(path), {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
                signal: controller.signal,
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                let message = `Upload mislukt (HTTP ${res.status})`;
                try { message = JSON.parse(text).error || message; } catch { /* geen JSON */ }
                if (res.status === 413 && message.startsWith('Upload mislukt')) { message = 'Bestand is te groot.'; }
                const err = new Error(message);
                err.statusCode = res.status;
                err.bodyText = text;
                throw err;
            }
            try { return await res.json(); }
            catch { return null; }
        } finally {
            clearTimeout(timeoutId);
            if (signal) { signal.removeEventListener('abort', onAbort); }
        }
    },

    /** Download blob (PDF). */
    async getBlob(path, { signal } = {}) {
        const res = await fetch(url(path), {
            method: 'GET',
            credentials: 'same-origin',
            signal,
        });
        if (!res.ok) {
            const err = new Error(`Download mislukt (HTTP ${res.status})`);
            err.statusCode = res.status;
            throw err;
        }
        return res.blob();
    },
};

export { setUnauthorizedHandler, registerErrorCodeHandler };

// Convenience endpoints voor de app.
export const endpoints = {
    me: '/api/me',
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    clusters: '/api/clusters',
    projects: '/api/projects',
    project: (id) => `/api/projects/${id}`,
    surveys: (id) => `/api/projects/${id}/surveys`,
    survey: (id, cluster, section) => `/api/projects/${id}/surveys/${cluster}/${section}`,
    photos: (id) => `/api/projects/${id}/photos`,
    validate: (id) => `/api/projects/${id}/validate`,
    risks: (id) => `/api/projects/${id}/risks`,
    variants: (id, clusterId) => `/api/projects/${id}/variants/${clusterId}`,
    risk: (riskId) => `/api/risks/${riskId}`,
    werkplan: (id) => `/api/projects/${id}/documents/werkplan`,
    opnamerapport: (id) => `/api/projects/${id}/documents/opnamerapport`,
    changePassword: '/api/auth/change-password',
    adminUsers: '/api/admin/users',
    adminUser: (id) => `/api/admin/users/${id}`,
    adminResetPassword: (id) => `/api/admin/users/${id}/reset-password`,
    complete: (id) => `/api/projects/${id}/complete`,
};
